import { fallbackGlossary } from "./glossary";
import { calculateManagementMetrics } from "../lib/calculations";
import type { DashboardPayload, InputField, MonthlyMetric, ScenarioId, SummaryMetrics } from "../types";

const sourceRows = [
  [1, 4.6763, 14.0290, 0, 0, 4.6763, 14.0290, 350724.86, 70144.97, 8754203.17, 6148985.71, 3085014.60, 0.3524],
  [2, 5.1440, 15.4319, 0, 2.8058, 9.8203, 26.6551, 736522.21, 133275.45, 9203130.99, 6482174.33, 3261078.48, 0.3543],
  [3, 5.6584, 16.9751, 0, 5.3310, 15.4787, 38.2992, 1160899.30, 191495.78, 9685728.41, 6837458.62, 3447453.67, 0.3559],
  [4, 6.2242, 18.6726, 0, 7.6598, 21.7029, 49.3119, 1627714.09, 246559.58, 10207607.00, 7219292.78, 3646630.32, 0.3572],
  [5, 6.8466, 20.5399, 0, 9.8624, 28.5495, 59.9894, 2141210.36, 299946.92, 10774490.61, 7632127.50, 3861055.79, 0.3584],
  [6, 7.5313, 22.5938, 0, 11.9979, 36.0808, 70.5853, 2706056.26, 352926.71, 11392316.31, 8080499.43, 4093188.72, 0.3593],
  [7, 8.2844, 24.8532, 1.4029, 14.1171, 42.9623, 81.3215, 3222169.29, 406607.47, 11962110.10, 8495459.31, 4308720.77, 0.3602],
  [8, 9.1128, 27.3385, 2.5252, 16.2643, 49.5499, 92.3957, 3716241.40, 461978.68, 12511553.42, 8896681.00, 4517637.30, 0.3611],
  [9, 10.0241, 30.0724, 3.4652, 18.4791, 56.1089, 103.9890, 4208164.17, 519944.92, 13061442.43, 9298993.18, 4727488.33, 0.3619],
  [10, 11.0265, 33.0796, 4.2929, 20.7978, 62.8425, 116.2708, 4713189.63, 581354.12, 13627877.08, 9713920.19, 4944163.21, 0.3628],
  [11, 12.1292, 36.3876, 5.0590, 23.2542, 69.9127, 129.4043, 5243454.92, 647021.29, 14223809.54, 10150773.07, 5172439.73, 0.3636],
  [12, 13.3421, 40.0264, 5.8007, 25.8809, 77.4542, 143.5498, 5809062.84, 717748.82, 14860145.00, 10617426.15, 5416375.40, 0.3645],
] as const;

const toMonthly = (rows: readonly (readonly number[])[]): MonthlyMetric[] => rows.map((row) => ({
  month: row[0],
  b2bNew: row[1],
  b2cNew: row[2],
  b2bChurn: row[3],
  b2cChurn: row[4],
  b2bActive: row[5],
  b2cActive: row[6],
  b2bMrr: row[7],
  b2cMrr: row[8],
  mrr: row[7] + row[8],
  revenue: row[9],
  grossProfit: row[10],
  operatingProfit: row[11],
  operatingMargin: row[12],
}));

const growthMonthly = toMonthly(sourceRows);

const uniformMonthly = toMonthly([
  [1, 8.3333, 25, 0, 0, 8.3333, 25, 625000, 125000, 9083333.33, 6395833.33, 3216666.67, 0.3541],
  [2, 8.3333, 25, 0, 5, 16.6667, 45, 1250000, 225000, 9808333.33, 6933333.33, 3500416.67, 0.3569],
  [3, 8.3333, 25, 0, 9, 25, 61, 1875000, 305000, 10513333.33, 7450833.33, 3771166.67, 0.3587],
  [4, 8.3333, 25, 0, 12.2, 33.3333, 73.8, 2500000, 369000, 11202333.33, 7952333.33, 4031516.67, 0.3599],
  [5, 8.3333, 25, 0, 14.76, 41.6667, 84.04, 3125000, 420200, 11878533.33, 8441033.33, 4283546.67, 0.3606],
  [6, 8.3333, 25, 0, 16.808, 50, 92.232, 3750000, 461160, 12544493.33, 8919493.33, 4528920.67, 0.3610],
  [7, 8.3333, 25, 2.5, 18.4464, 55.8333, 98.7856, 4187500, 493928, 13014761.33, 9258511.33, 4703344.87, 0.3614],
  [8, 8.3333, 25, 4.25, 19.7571, 59.9167, 104.0285, 4493750, 520142.4, 13347225.73, 9499100.73, 4827571.73, 0.3617],
  [9, 8.3333, 25, 5.475, 20.8057, 62.775, 108.2228, 4708125, 541113.92, 13582572.25, 9670134.75, 4916234.46, 0.3620],
  [10, 8.3333, 25, 6.3325, 21.6446, 64.7758, 111.5782, 4858187.5, 557891.14, 13749411.97, 9791955.72, 4979661.53, 0.3622],
  [11, 8.3333, 25, 6.93275, 22.3156, 66.1764, 114.2626, 4963231.25, 571312.91, 13867877.49, 9878908.12, 5025150.99, 0.3624],
  [12, 8.3333, 25, 7.352925, 22.8525, 67.1568, 116.4101, 5036761.88, 582050.33, 13952145.54, 9941116.97, 5057866.04, 0.3625],
]);

const actualMonthly = toMonthly(Array.from({ length: 12 }, (_, index) => [index + 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]));

const inputs: InputField[] = [
  { key: "b2bTarget", label: "BtoB年間新規獲得目標", value: 100, unit: "件/年", group: "成長前提" },
  { key: "b2bGrowth", label: "BtoB月次獲得成長率", value: 0.1, unit: "%/月", group: "成長前提" },
  { key: "b2bChurn", label: "BtoB契約終了後の月次解約率", value: 0.3, unit: "%/月", group: "継続前提", helpId: "churn_rate" },
  { key: "b2cTarget", label: "BtoC年間新規獲得目標", value: 300, unit: "件/年", group: "成長前提" },
  { key: "b2cGrowth", label: "BtoC月次獲得成長率", value: 0.1, unit: "%/月", group: "成長前提" },
  { key: "b2cChurn", label: "BtoC月次解約率", value: 0.2, unit: "%/月", group: "継続前提", helpId: "churn_rate" },
  { key: "systemRevenue", label: "システム開発年間売上目標", value: 100000000, unit: "円/年", group: "売上前提" },
  { key: "monthlyOfficerComp", label: "役員報酬", value: 0, unit: "円/月", group: "社内人件費" },
  { key: "monthlySalary", label: "社員給与", value: 0, unit: "円/月", group: "社内人件費" },
  { key: "monthlyBenefits", label: "法定福利費", value: 0, unit: "円/月", group: "社内人件費" },
  { key: "fte", label: "社内稼働人数", value: 0, unit: "FTE", group: "生産性", helpId: "revenue_per_fte" },
  { key: "annualHours", label: "年間総人時", value: 0, unit: "時間/年", group: "生産性", helpId: "labor_productivity" },
  { key: "annualDepreciation", label: "年間減価償却費", value: 0, unit: "円/年", group: "会計調整", helpId: "ebitda" },
  { key: "nonOperatingIncome", label: "年間営業外収益", value: 0, unit: "円/年", group: "会計調整" },
  { key: "interestExpense", label: "年間支払利息", value: 0, unit: "円/年", group: "会計調整" },
  { key: "extraordinaryIncome", label: "年間特別利益", value: 0, unit: "円/年", group: "会計調整" },
  { key: "extraordinaryLoss", label: "年間特別損失", value: 0, unit: "円/年", group: "会計調整" },
  { key: "taxes", label: "年間法人税等", value: 0, unit: "円/年", group: "会計調整" },
  { key: "cash", label: "現預金残高", value: 0, unit: "円", group: "財政状態" },
  { key: "receivables", label: "売掛金残高", value: 0, unit: "円", group: "財政状態" },
  { key: "inventory", label: "棚卸資産残高", value: 0, unit: "円", group: "財政状態" },
  { key: "payables", label: "買掛金残高", value: 0, unit: "円", group: "財政状態" },
  { key: "interestBearingDebt", label: "有利子負債残高", value: 0, unit: "円", group: "財政状態" },
  { key: "totalAssets", label: "総資産", value: 0, unit: "円", group: "財政状態" },
  { key: "equity", label: "純資産", value: 0, unit: "円", group: "財政状態" },
  { key: "currentAssets", label: "流動資産", value: 0, unit: "円", group: "財政状態" },
  { key: "currentLiabilities", label: "流動負債", value: 0, unit: "円", group: "財政状態" },
  { key: "b2bAcquisitionCost", label: "BtoB年間顧客獲得費", value: 0, unit: "円/年", group: "顧客獲得", helpId: "cac" },
  { key: "b2cAcquisitionCost", label: "BtoC年間顧客獲得費", value: 0, unit: "円/年", group: "顧客獲得", helpId: "cac" },
  { key: "openingReceivables", label: "期首売掛金", value: 0, unit: "円", group: "資金繰り", helpId: "working_capital_change" },
  { key: "openingInventory", label: "期首棚卸資産", value: 0, unit: "円", group: "資金繰り", helpId: "working_capital_change" },
  { key: "openingPayables", label: "期首買掛金", value: 0, unit: "円", group: "資金繰り", helpId: "working_capital_change" },
  { key: "annualCapex", label: "年間設備投資額", value: 0, unit: "円/年", group: "資金繰り", helpId: "fcf_proxy" },
  { key: "annualPrincipalRepayment", label: "年間借入元本返済額", value: 0, unit: "円/年", group: "資金繰り", helpId: "dscr" },
  { key: "priorYearRevenue", label: "前期売上高", value: 0, unit: "円/年", group: "公的診断・財務", helpId: "local_sales_growth" },
  { key: "effectiveTaxRate", label: "想定実効税率", value: 0, unit: "%", group: "公的診断・財務", helpId: "nopat", max: 1, step: 0.001 },
  { key: "wacc", label: "WACC", value: 0, unit: "%", group: "公的診断・財務", helpId: "wacc", max: 1, step: 0.001 },
  { key: "humanCapitalInvestment", label: "年間人的資本投資額", value: 0, unit: "円/年", group: "企業価値・成長投資", helpId: "human_capital_investment" },
  { key: "dxRdInvestment", label: "年間DX・研究開発投資額", value: 0, unit: "円/年", group: "企業価値・成長投資", helpId: "growth_investment_rate" },
  { key: "annualTrainingHours", label: "年間研修時間", value: 0, unit: "時間/年", group: "企業価値・成長投資", helpId: "human_capital_investment" },
  { key: "wageGrowthRate", label: "賃金増加率", value: 0, unit: "%", group: "企業価値・成長投資", helpId: "human_capital_investment", max: 1, step: 0.001 },
  { key: "voluntaryTurnoverRate", label: "自主都合離職率", value: 0, unit: "%", group: "企業価値・成長投資", helpId: "voluntary_turnover", max: 1, step: 0.001 },
  { key: "engagementScore", label: "従業員エンゲージメント", value: 0, unit: "点", group: "企業価値・成長投資", helpId: "engagement_score", max: 100, step: 0.1 },
  { key: "managementMaturity", label: "経営者の成熟度", value: 0, unit: "0〜5", group: "ロカベン非財務", helpId: "nonfinancial_maturity", max: 5, step: 0.1 },
  { key: "businessMaturity", label: "事業の成熟度", value: 0, unit: "0〜5", group: "ロカベン非財務", helpId: "nonfinancial_maturity", max: 5, step: 0.1 },
  { key: "stakeholderMaturity", label: "環境・関係者の成熟度", value: 0, unit: "0〜5", group: "ロカベン非財務", helpId: "nonfinancial_maturity", max: 5, step: 0.1 },
  { key: "internalControlMaturity", label: "内部管理体制の成熟度", value: 0, unit: "0〜5", group: "ロカベン非財務", helpId: "nonfinancial_maturity", max: 5, step: 0.1 },
  { key: "healthDeclaration", label: "健康宣言を社内外へ発信", value: 0, unit: "0/1", group: "健康経営・体制", helpId: "health_management_readiness", max: 1, step: 1 },
  { key: "executiveHealthCheck", label: "経営者が健診を受診", value: 0, unit: "0/1", group: "健康経営・体制", helpId: "health_management_readiness", max: 1, step: 1 },
  { key: "healthLeader", label: "健康づくり担当者を設置", value: 0, unit: "0/1", group: "健康経営・体制", helpId: "health_management_readiness", max: 1, step: 1 },
  { key: "healthDataReady", label: "40歳以上の健診データ提供体制", value: 0, unit: "0/1", group: "健康経営・体制", helpId: "health_management_readiness", max: 1, step: 1 },
  { key: "healthPlan", label: "健康経営の具体的推進計画", value: 0, unit: "0/1", group: "健康経営・体制", helpId: "health_management_readiness", max: 1, step: 1 },
  { key: "healthIssueActions", label: "健康課題把握の実施項目数", value: 0, unit: "/3", group: "健康経営・取組", helpId: "health_management_readiness", max: 3, step: 1 },
  { key: "foundationActions", label: "健康経営の土台づくり実施項目数", value: 0, unit: "/7", group: "健康経営・取組", helpId: "health_management_readiness", max: 7, step: 1 },
  { key: "promotionActions", label: "心身の健康づくり実施項目数", value: 0, unit: "/7", group: "健康経営・取組", helpId: "health_management_readiness", max: 7, step: 1 },
  { key: "passiveSmokingControl", label: "受動喫煙対策", value: 0, unit: "0/1", group: "健康経営・取組", helpId: "health_management_readiness", max: 1, step: 1 },
  { key: "healthReview", label: "取組の評価・改善", value: 0, unit: "0/1", group: "健康経営・取組", helpId: "health_management_readiness", max: 1, step: 1 },
  { key: "healthCompliance", label: "法令遵守・リスク管理", value: 0, unit: "0/1", group: "健康経営・取組", helpId: "health_management_readiness", max: 1, step: 1 },
  { key: "healthCheckRate", label: "定期健診受診率", value: 0, unit: "%", group: "健康経営・成果", helpId: "health_check_rate", max: 1, step: 0.001 },
  { key: "stressCheckRate", label: "ストレスチェック実施率", value: 0, unit: "%", group: "健康経営・成果", helpId: "health_management_readiness", max: 1, step: 0.001 },
  { key: "paidLeaveRate", label: "年次有給休暇取得率", value: 0, unit: "%", group: "健康経営・成果", helpId: "health_management_readiness", max: 1, step: 0.001 },
  { key: "overtimeHours", label: "月平均法定外労働時間", value: 0, unit: "時間/月", group: "健康経営・成果", helpId: "health_management_readiness", max: 744, step: 0.1 },
  { key: "healthInvestment", label: "年間健康投資額", value: 0, unit: "円/年", group: "健康経営・成果", helpId: "health_investment" },
];

const growthSummary: SummaryMetrics = {
  revenue: 140264414.06326032,
  costOfSales: 40690622.80631501,
  grossProfit: 99573791.25694528,
  grossMargin: 0.7099005968259116,
  operatingProfit: 50481246.33480417,
  operatingMargin: 0.3599005968259116,
  mrr: 6526811.665678233,
  recurringRevenue: 40264414.063260294,
  yearEndActive: 221.0039357947876,
  b2bTargetRate: 1,
  b2cTargetRate: 1,
};

const uniformSummary: SummaryMetrics = {
  revenue: 146544354.31684,
  costOfSales: 42411766.6875,
  grossProfit: 104132587.62934,
  grossMargin: 0.710587508572302,
  operatingProfit: 52842063.618446,
  operatingMargin: 0.360587508572302,
  mrr: 5618812.20204,
  recurringRevenue: uniformMonthly.reduce((sum, row) => sum + row.mrr, 0),
  yearEndActive: 183.566890408,
  b2bTargetRate: 1,
  b2cTargetRate: 1,
};

const actualSummary: SummaryMetrics = {
  revenue: 0,
  costOfSales: 0,
  grossProfit: 0,
  grossMargin: 0,
  operatingProfit: 0,
  operatingMargin: 0,
  mrr: 0,
  recurringRevenue: 0,
  yearEndActive: 0,
  b2bTargetRate: 0,
  b2cTargetRate: 0,
};

export function createDemoPayload(scenario: ScenarioId = "growth", overrides: Record<string, number> = {}): DashboardPayload {
  const nextInputs = inputs.map((item) => ({ ...item, value: overrides[item.key] ?? item.value }));
  const scenarioData = scenario === "uniform"
    ? { summary: uniformSummary, monthly: uniformMonthly, variableCosts: 42411766.6875 + 29308870.863368 + 14654435.431684 + 7327217.715842, annualNew: 400 }
    : scenario === "actual"
      ? { summary: actualSummary, monthly: actualMonthly, variableCosts: 0, annualNew: 0 }
      : { summary: growthSummary, monthly: growthMonthly, variableCosts: 40690622.80631501 + 28052882.812652063 + 14026441.406326031 + 7013220.703163016, annualNew: 400 };
  const calculated = calculateManagementMetrics(
    scenarioData.summary,
    scenarioData.variableCosts,
    0,
    nextInputs,
    scenarioData.annualNew,
    scenarioData.monthly,
    {
      target: { summary: growthSummary, monthly: growthMonthly },
      actual: { summary: actualSummary, monthly: actualMonthly },
    },
  );
  return {
    scenario,
    scenarioLabel: scenario === "uniform" ? "均等シナリオ" : scenario === "actual" ? "現在実績" : "成長シナリオ",
    scenarios: [
      { id: "growth", label: "成長シナリオ" },
      { id: "uniform", label: "均等シナリオ" },
      { id: "actual", label: "現在実績" },
    ],
    updatedAt: new Date().toISOString(),
    modelStatus: "PASS",
    source: "demo",
    summary: scenarioData.summary,
    monthly: scenarioData.monthly,
    glossary: fallbackGlossary,
    inputs: nextInputs,
    ...calculated,
  };
}
